import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router";
import { FormattedMessage, useIntl } from "react-intl";
import {
  CalendarDays,
  ChartNoAxesCombined,
  CheckCircle,
  ChevronDown,
  ImageIcon,
  LayoutDashboard,
  Loader2,
  Megaphone,
  MessageSquare,
  Plug,
  ChevronLeft,
  ChevronRight,
  Settings,
  Sparkles,
  Star,
  Plus,
  MoreHorizontal,
  PencilLine,
  Trash2,
  UsersRound,
} from "lucide-react";
import type { ComponentType, KeyboardEvent, MouseEvent } from "react";

import { CHEETAH_LOGO_SRC, CHEETAH_SQUARE_LOGO_SRC } from "@/lib/brand-assets";
import {
  getBrandAvatarStyle,
  getBrandInitials,
  getSidebarBrandContext,
  getBrandSwitcherSubtitle,
  getBrandSwitcherTitle,
  type BrandSwitcherBrand,
} from "@/lib/brand-switcher";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { ACCOUNT_PROFILE_SETTINGS_PATH } from "@/lib/app-route-manifest";
import {
  Avatar,
  AvatarFallback,
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@runcheetah/ui";
import { useAuthenticatedUser } from "@/hooks/use-authenticated-user";
import { useAssetCreationGate } from "@/hooks/use-asset-creation-gate";
import { buildBrandPath, getBrandPathForStatus } from "@/hooks/use-route-brand";
import { isCalendarPostUnapproved } from "@/lib/calendar-posts";
import {
  buildBrandAssistantChatPath,
  buildBrandAssistantNewChatPath,
  type BrandAssistantChatSummary,
} from "@/lib/brand-assistant-chat-history";
import { SidebarCreateModal } from "@/components/layout/sidebar-create-modal";
import { SidebarProfileMenu } from "@/components/layout/sidebar-profile-menu";
import { toast } from "sonner";
import {
  INSTAGRAM_INTEGRATION_SECTION,
  INTEGRATIONS_SECTION,
  isSidebarExactPathActive,
  isSidebarNestedPathActive,
  NAV_ITEMS,
  type NavItem,
} from "@/components/layout/app-sidebar-nav";

const NAV_ITEM_ICONS: Record<
  NavItem["icon"],
  ComponentType<{ className?: string; strokeWidth?: number }>
> = {
  dashboard: LayoutDashboard,
  review: CheckCircle,
  calendar: CalendarDays,
  campaigns: Megaphone,
  insights: ChartNoAxesCombined,
};

const BRAND_KIT_ITEMS = [
  { labelId: "sidebar.brandFocus", labelDefault: "Content Focus", section: "/focus" },
  { labelId: "sidebar.brandKit.style", labelDefault: "Brand Style", section: "/brand-kit/style" },
  {
    labelId: "sidebar.brandKit.visualUniverse",
    labelDefault: "Visual Universe",
    section: "/brand-kit/visual-universe",
  },
  { labelId: "sidebar.brandKit.voice", labelDefault: "Brand Voice", section: "/brand-kit/voice" },
  {
    labelId: "sidebar.brandKit.profile",
    labelDefault: "Brand Profile",
    section: "/brand-kit/profile",
  },
  {
    labelId: "sidebar.brandKit.contentPreferences",
    labelDefault: "Content Preferences",
    section: "/brand-kit/content-preferences",
  },
  { labelId: "sidebar.brandKit.mediaLibrary", labelDefault: "Media Library", section: "/assets" },
  {
    labelId: "sidebar.brandKit.sourceMaterials",
    labelDefault: "Source Materials",
    section: "/brand-kit/source-materials",
  },
];

const CHAT_PAGE_SIZE = 3;

type ChatListCursor = {
  updatedAt: string;
  id: string;
};

function BrandMark({
  brand,
  title,
  size,
}: {
  brand: BrandSwitcherBrand;
  title: string;
  size: "sm" | "md";
}) {
  const avatarStyle = getBrandAvatarStyle(brand);

  return (
    <Avatar
      className={cn(
        "rounded-xl border shadow-[inset_0_1px_0_rgba(255,255,255,0.14)]",
        size === "md" ? "h-9 w-9" : "h-8 w-8",
        avatarStyle.className,
      )}
      style={avatarStyle.style}
    >
      <AvatarFallback
        className={cn(
          "rounded-xl bg-transparent font-semibold uppercase",
          size === "md" ? "text-base" : "text-sm",
        )}
        style={avatarStyle.style}
      >
        {getBrandInitials(title)}
      </AvatarFallback>
    </Avatar>
  );
}

export function AppSidebar({
  collapsed,
  onCollapsedChange,
  showAssistantAction = false,
  onOpenAssistant,
}: {
  collapsed: boolean;
  onCollapsedChange: (collapsed: boolean) => void;
  showAssistantAction?: boolean;
  onOpenAssistant?: () => void;
}) {
  const intl = useIntl();
  const location = useLocation();
  const navigate = useNavigate();
  const { brandId: routeBrandId, chatId: routeChatId } = useParams<{
    brandId: string;
    chatId?: string;
  }>();
  const utils = trpc.useUtils();
  const [createOpen, setCreateOpen] = useState(false);
  const { user } = useAuthenticatedUser();
  const [isBrandSwitcherOpen, setIsBrandSwitcherOpen] = useState(false);
  const [isBrandKitOpen, setIsBrandKitOpen] = useState(false);
  const [additionalChats, setAdditionalChats] = useState<BrandAssistantChatSummary[]>([]);
  const [nextChatCursor, setNextChatCursor] = useState<ChatListCursor | null>(null);
  const [isLoadingMoreChats, setIsLoadingMoreChats] = useState(false);
  const [chatMenuId, setChatMenuId] = useState<string | null>(null);
  const [renamingChatId, setRenamingChatId] = useState<string | null>(null);
  const [renameDraft, setRenameDraft] = useState("");
  const [deleteCandidate, setDeleteCandidate] = useState<BrandAssistantChatSummary | null>(null);
  const [chatActionError, setChatActionError] = useState<string | null>(null);
  const brandSwitcherRef = useRef<HTMLDivElement | null>(null);
  const brandListQuery = trpc.brand.listMine.useQuery(undefined, {
    staleTime: 0,
    refetchOnWindowFocus: true,
  });
  const brands = brandListQuery.data?.brands ?? [];
  const profileContextBrandId =
    location.pathname === ACCOUNT_PROFILE_SETTINGS_PATH
      ? (new URLSearchParams(location.search).get("brandId") ?? undefined)
      : undefined;
  const { currentBrand, sidebarBrandId } = getSidebarBrandContext({
    brands,
    routeBrandId,
    contextBrandId: profileContextBrandId,
  });
  const canUseBrandNavigation = currentBrand?.status === "scanned";
  const canFetchAudienceProfiles = Boolean(sidebarBrandId) && canUseBrandNavigation;
  const assetCreationGate = useAssetCreationGate(sidebarBrandId);
  const audienceProfileQuery = trpc.audienceProfile.list.useQuery(
    { brandId: sidebarBrandId ?? "" },
    {
      enabled: canFetchAudienceProfiles,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    },
  );
  const chatListQuery = trpc.brandAssistantChat.list.useQuery(
    { brandId: sidebarBrandId ?? "", limit: CHAT_PAGE_SIZE },
    {
      enabled: Boolean(sidebarBrandId),
      staleTime: 15_000,
      refetchInterval: 15_000,
      refetchOnWindowFocus: true,
    },
  );

  const updateCachedChatLists = (
    brandId: string,
    updater: (chats: BrandAssistantChatSummary[]) => BrandAssistantChatSummary[],
  ) => {
    const updateList = (input: { brandId: string; limit?: number }) => {
      utils.brandAssistantChat.list.setData(input, (current) =>
        current ? { ...current, chats: updater(current.chats) } : current,
      );
    };

    updateList({ brandId });
    updateList({ brandId, limit: CHAT_PAGE_SIZE });
    setAdditionalChats((currentChats) => updater(currentChats));
  };

  const invalidateChatQueries = async (brandId: string, chatId: string) => {
    await Promise.all([
      utils.brandAssistantChat.list.invalidate({ brandId }),
      utils.brandAssistantChat.get.invalidate({ brandId, chatId }),
      utils.brandAssistantChat.generatedAssets.invalidate({ brandId, chatId }),
      utils.brandAssistantChat.generatedImages.invalidate({ brandId, chatId }),
    ]);
  };

  const renameChatMutation = trpc.brandAssistantChat.rename.useMutation({
    onSuccess(data, variables) {
      updateCachedChatLists(variables.brandId, (currentChats) =>
        currentChats.map((chat) =>
          chat.id === variables.chatId ? { ...chat, ...data.chat, title: data.chat.title } : chat,
        ),
      );
      void invalidateChatQueries(variables.brandId, variables.chatId);
    },
  });
  const deleteChatMutation = trpc.brandAssistantChat.delete.useMutation({
    onSuccess(data, variables) {
      updateCachedChatLists(variables.brandId, (currentChats) =>
        currentChats.filter((chat) => chat.id !== data.chatId),
      );
      void invalidateChatQueries(variables.brandId, variables.chatId);
    },
  });
  const setActiveAudienceProfileMutation = trpc.audienceProfile.setActive.useMutation({
    onSuccess(_data, variables) {
      void utils.audienceProfile.list.invalidate({ brandId: variables.brandId });
    },
  });

  const now = useMemo(() => new Date(), []);
  const pendingRangeStart = useMemo(() => {
    const d = new Date(now);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [now]);
  const pendingRangeEnd = useMemo(() => {
    const d = new Date(pendingRangeStart);
    d.setDate(d.getDate() + 60);
    return d;
  }, [pendingRangeStart]);
  const pendingPostsQuery = trpc.calendar.list.useQuery(
    {
      brandId: sidebarBrandId ?? "",
      start: pendingRangeStart.toISOString(),
      end: pendingRangeEnd.toISOString(),
      approvalStatuses: ["pending"],
    },
    { enabled: Boolean(sidebarBrandId), refetchOnWindowFocus: true, staleTime: 60_000 },
  );
  const instagramConnectionQuery = trpc.socialConnection.getInstagram.useQuery(
    { brandId: sidebarBrandId ?? "" },
    { enabled: Boolean(sidebarBrandId), refetchOnWindowFocus: true, staleTime: 60_000 },
  );
  const pendingCount = pendingPostsQuery.data?.filter(isCalendarPostUnapproved).length ?? 0;
  const hasUnseenAssistantContent = chatListQuery.data?.hasUnseenAssistantContent ?? false;
  const isBrandKitRoute =
    (routeBrandId !== undefined && location.pathname === buildBrandPath(routeBrandId, "/focus")) ||
    location.pathname.includes("/brand-kit/") ||
    (routeBrandId !== undefined && location.pathname === buildBrandPath(routeBrandId, "/assets"));

  useEffect(() => {
    if (isBrandKitRoute) {
      setIsBrandKitOpen(true);
    }
  }, [isBrandKitRoute]);

  useEffect(() => {
    setAdditionalChats([]);
    setNextChatCursor(null);
    setIsLoadingMoreChats(false);
  }, [sidebarBrandId]);

  useEffect(() => {
    setAdditionalChats([]);
    setNextChatCursor(chatListQuery.data?.nextCursor ?? null);
  }, [chatListQuery.data]);

  useEffect(() => {
    if (!isBrandSwitcherOpen) {
      return;
    }

    const handlePointerDown = (event: PointerEvent) => {
      if (brandSwitcherRef.current?.contains(event.target as Node)) {
        return;
      }

      setIsBrandSwitcherOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
    };
  }, [isBrandSwitcherOpen]);

  if (!user) return null;

  const currentBrandTitle = currentBrand
    ? getBrandSwitcherTitle(currentBrand)
    : intl.formatMessage({
        id: "sidebar.brandSwitcher.selectBrand",
        defaultMessage: "Select brand",
      });
  const currentBrandSubtitle = currentBrand
    ? getBrandSwitcherSubtitle(currentBrand, intl)
    : intl.formatMessage({
        id: "sidebar.brandSwitcher.noBrandSelected",
        defaultMessage: "No brand selected",
      });
  const addBrandHref = sidebarBrandId ? buildBrandPath(sidebarBrandId, "/new") : "/app";
  const addBrandDisabled = brandListQuery.data ? !brandListQuery.data.canCreate : true;
  const integrationsHref = sidebarBrandId
    ? buildBrandPath(sidebarBrandId, INTEGRATIONS_SECTION)
    : "/app";
  const instagramIntegrationHref = sidebarBrandId
    ? buildBrandPath(sidebarBrandId, INSTAGRAM_INTEGRATION_SECTION)
    : "/app";
  const hasInstagramConnection = Boolean(instagramConnectionQuery.data);
  const isIntegrationsActive =
    routeBrandId !== undefined && isSidebarExactPathActive(location.pathname, integrationsHref);
  const isInstagramIntegrationActive =
    routeBrandId !== undefined &&
    isSidebarNestedPathActive(location.pathname, instagramIntegrationHref);
  const toggleSidebarLabel = collapsed
    ? intl.formatMessage({ id: "sidebar.expand", defaultMessage: "Expand sidebar" })
    : intl.formatMessage({ id: "sidebar.collapse", defaultMessage: "Collapse sidebar" });
  const chats = [...(chatListQuery.data?.chats ?? []), ...additionalChats];
  const canShowMoreChats = Boolean(nextChatCursor);
  const canCreateChat = canUseBrandNavigation;
  const audienceProfiles = audienceProfileQuery.data?.profiles ?? [];
  const activeAudienceProfile = audienceProfileQuery.data?.activeProfile ?? null;
  const latestChatHref = sidebarBrandId
    ? chats[0]
      ? buildBrandAssistantChatPath(sidebarBrandId, chats[0].id)
      : buildBrandPath(sidebarBrandId, "/pulse")
    : "/app";

  const startRenamingChat = (chat: BrandAssistantChatSummary) => {
    setChatActionError(null);
    setChatMenuId(null);
    setRenamingChatId(chat.id);
    setRenameDraft(chat.title);
  };

  const cancelRename = () => {
    setRenamingChatId(null);
    setRenameDraft("");
  };

  const saveRename = async (chat: BrandAssistantChatSummary) => {
    if (!sidebarBrandId || renameChatMutation.isPending) {
      return;
    }

    const title = renameDraft.replace(/\s+/g, " ").trim();
    if (!title || title === chat.title) {
      cancelRename();
      return;
    }

    setChatActionError(null);
    updateCachedChatLists(sidebarBrandId, (currentChats) =>
      currentChats.map((item) => (item.id === chat.id ? { ...item, title } : item)),
    );
    cancelRename();

    try {
      await renameChatMutation.mutateAsync({ brandId: sidebarBrandId, chatId: chat.id, title });
    } catch (error) {
      updateCachedChatLists(sidebarBrandId, (currentChats) =>
        currentChats.map((item) => (item.id === chat.id ? { ...item, title: chat.title } : item)),
      );
      setChatActionError(
        error instanceof Error
          ? error.message
          : intl.formatMessage({
              id: "sidebar.chats.renameError",
              defaultMessage: "Could not rename this chat.",
            }),
      );
    }
  };

  const handleRenameKeyDown = (
    event: KeyboardEvent<HTMLInputElement>,
    chat: BrandAssistantChatSummary,
  ) => {
    if (event.key === "Enter") {
      event.preventDefault();
      void saveRename(chat);
      return;
    }

    if (event.key === "Escape") {
      event.preventDefault();
      setRenameDraft(chat.title);
      cancelRename();
    }
  };

  const confirmDeleteChat = async () => {
    if (!sidebarBrandId || !deleteCandidate || deleteChatMutation.isPending) {
      return;
    }

    const chatToDelete = deleteCandidate;
    const remainingChats = chats.filter((chat) => chat.id !== chatToDelete.id);
    setChatActionError(null);
    updateCachedChatLists(sidebarBrandId, (currentChats) =>
      currentChats.filter((chat) => chat.id !== chatToDelete.id),
    );

    try {
      await deleteChatMutation.mutateAsync({ brandId: sidebarBrandId, chatId: chatToDelete.id });
      setDeleteCandidate(null);

      if (routeChatId === chatToDelete.id) {
        const nextChat = remainingChats[0];
        navigate(
          nextChat
            ? buildBrandAssistantChatPath(sidebarBrandId, nextChat.id)
            : buildBrandPath(sidebarBrandId, "/pulse"),
          { replace: true },
        );
      }
    } catch (error) {
      updateCachedChatLists(sidebarBrandId, (currentChats) => {
        if (currentChats.some((chat) => chat.id === chatToDelete.id)) {
          return currentChats;
        }

        return [chatToDelete, ...currentChats];
      });
      setChatActionError(
        error instanceof Error
          ? error.message
          : intl.formatMessage({
              id: "sidebar.chats.deleteError",
              defaultMessage: "Could not delete this chat.",
            }),
      );
    }
  };

  const createChat = () => {
    if (!sidebarBrandId || !canCreateChat) {
      return;
    }

    navigate(buildBrandAssistantNewChatPath(sidebarBrandId));
  };

  const activateAudienceProfileFromSidebar = async (profileId: string) => {
    if (!sidebarBrandId || !profileId) {
      return;
    }

    try {
      await setActiveAudienceProfileMutation.mutateAsync({
        brandId: sidebarBrandId,
        id: profileId,
      });
    } catch {
      toast.error(
        intl.formatMessage({
          id: "sidebar.audienceProfile.activateFailed",
          defaultMessage: "Could not activate that audience profile.",
        }),
      );
    }
  };

  const showMoreChats = async () => {
    if (!sidebarBrandId || !nextChatCursor || isLoadingMoreChats) {
      return;
    }

    setIsLoadingMoreChats(true);
    try {
      const page = await utils.brandAssistantChat.list.fetch({
        brandId: sidebarBrandId,
        limit: CHAT_PAGE_SIZE,
        cursor: nextChatCursor,
      });
      setAdditionalChats((currentChats) => {
        const knownIds = new Set([
          ...(chatListQuery.data?.chats ?? []).map((chat) => chat.id),
          ...currentChats.map((chat) => chat.id),
        ]);
        return [...currentChats, ...page.chats.filter((chat) => !knownIds.has(chat.id))];
      });
      setNextChatCursor(page.nextCursor);
    } finally {
      setIsLoadingMoreChats(false);
    }
  };
  const preventBlockedBrandNavigation = (event: MouseEvent) => {
    if (!canUseBrandNavigation) {
      event.preventDefault();
    }
  };
  const audienceProfileLabel = intl.formatMessage({
    id: "sidebar.audienceProfile.label",
    defaultMessage: "Audience profile",
  });
  const audienceProfilePlaceholder = intl.formatMessage({
    id: "sidebar.audienceProfile.none",
    defaultMessage: "No active profile",
  });
  const audienceProfileSelector = (
    <div className={cn("mt-1 px-0.5 pb-1 pt-0.5", collapsed && "flex justify-center px-0")}>
      {canFetchAudienceProfiles && audienceProfileQuery.isPending ? (
        <div
          className={cn(
            "flex h-8 items-center gap-2 rounded-md px-2.5 text-xs font-medium text-muted-foreground",
            collapsed && "w-8 justify-center px-0",
          )}
          aria-label={intl.formatMessage({
            id: "sidebar.audienceProfile.loading",
            defaultMessage: "Loading profiles...",
          })}
          title={audienceProfileLabel}
        >
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          {collapsed ? null : (
            <span className="truncate">
              <FormattedMessage
                id="sidebar.audienceProfile.loading"
                defaultMessage="Loading profiles..."
              />
            </span>
          )}
        </div>
      ) : audienceProfiles.length > 0 ? (
        <Select
          value={activeAudienceProfile?.id}
          onValueChange={(profileId) => void activateAudienceProfileFromSidebar(profileId)}
          disabled={!canUseBrandNavigation || setActiveAudienceProfileMutation.isPending}
        >
          <SelectTrigger
            className={cn(
              "h-8 rounded-md border-transparent bg-transparent px-2.5 text-xs font-medium text-muted-foreground shadow-none transition-colors hover:bg-muted/50 hover:text-foreground focus:ring-0 focus:ring-offset-0 data-[state=open]:bg-muted/50 data-[state=open]:text-foreground [&>svg]:h-3.5 [&>svg]:w-3.5 [&>svg]:opacity-35",
              collapsed && "w-8 justify-center px-0",
            )}
            aria-label={audienceProfileLabel}
            title={
              activeAudienceProfile
                ? `${audienceProfileLabel}: ${activeAudienceProfile.name}`
                : audienceProfilePlaceholder
            }
          >
            {collapsed ? (
              <>
                <UsersRound className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="sr-only">
                  <SelectValue placeholder={audienceProfilePlaceholder} />
                </span>
              </>
            ) : (
              <div className="flex min-w-0 flex-1 items-center gap-2">
                <UsersRound className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
                <span className="min-w-0 flex-1 truncate text-left">
                  <SelectValue placeholder={audienceProfilePlaceholder} />
                </span>
              </div>
            )}
          </SelectTrigger>
          <SelectContent>
            {audienceProfiles.map((profile) => (
              <SelectItem key={profile.id} value={profile.id}>
                {profile.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      ) : (
        <div
          className={cn(
            "flex h-8 items-center gap-2 rounded-md px-2.5 text-xs font-medium text-muted-foreground",
            collapsed && "w-8 justify-center px-0",
          )}
          aria-label={audienceProfilePlaceholder}
          title={audienceProfilePlaceholder}
        >
          <UsersRound className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          {collapsed ? null : <span className="truncate">{audienceProfilePlaceholder}</span>}
        </div>
      )}
    </div>
  );

  return (
    <aside
      className={cn(
        "relative flex h-full min-h-0 shrink-0 flex-col border-r border-border bg-card transition-[width] duration-200 ease-out",
        collapsed ? "w-16" : "w-60",
      )}
    >
      {/* Logo */}
      <div
        className={cn(
          "flex shrink-0 items-center gap-2 py-5",
          collapsed ? "flex-col px-3" : "justify-between px-5",
        )}
      >
        <a
          href="/"
          className="flex min-w-0 items-center"
          aria-label={intl.formatMessage({ id: "common.brandName", defaultMessage: "Cheetah" })}
        >
          <img
            src={collapsed ? CHEETAH_SQUARE_LOGO_SRC : CHEETAH_LOGO_SRC}
            alt={intl.formatMessage({ id: "common.brandName", defaultMessage: "Cheetah" })}
            className={cn(collapsed ? "size-7 rounded-md" : "h-7 w-auto")}
          />
        </a>
        <button
          type="button"
          onClick={() => onCollapsedChange(!collapsed)}
          className={cn(
            "flex size-8 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground",
            collapsed && "mt-1",
          )}
          aria-label={toggleSidebarLabel}
          title={toggleSidebarLabel}
        >
          {collapsed ? (
            <ChevronRight className="h-4 w-4" aria-hidden="true" />
          ) : (
            <ChevronLeft className="h-4 w-4" aria-hidden="true" />
          )}
        </button>
      </div>

      {/* Brand switcher */}
      <div
        ref={brandSwitcherRef}
        className={cn("relative z-30 shrink-0 pb-3", collapsed ? "px-3" : "px-4")}
        onBlur={(event) => {
          const nextFocus = event.relatedTarget;
          if (nextFocus instanceof Node && event.currentTarget.contains(nextFocus)) {
            return;
          }

          setIsBrandSwitcherOpen(false);
        }}
      >
        <button
          type="button"
          onClick={() => setIsBrandSwitcherOpen((current) => !current)}
          className={cn(
            "relative z-10 flex w-full items-center gap-3 rounded-xl border border-border bg-card p-3 text-left shadow-none transition-colors hover:bg-muted/60",
            collapsed && "justify-center p-1",
            isBrandSwitcherOpen && "bg-muted/60",
          )}
          aria-expanded={isBrandSwitcherOpen}
          aria-label={currentBrandTitle}
          title={currentBrandTitle}
        >
          {currentBrand ? (
            <BrandMark
              brand={currentBrand}
              title={currentBrandTitle}
              size={collapsed ? "sm" : "md"}
            />
          ) : (
            <Avatar
              className={cn(
                "rounded-xl border border-transparent bg-primary text-primary-foreground",
                collapsed ? "h-8 w-8" : "h-9 w-9",
              )}
            >
              <AvatarFallback className="rounded-xl bg-transparent text-base font-semibold uppercase">
                {getBrandInitials(currentBrandTitle)}
              </AvatarFallback>
            </Avatar>
          )}
          {collapsed ? null : (
            <>
              <span className="min-w-0 flex-1">
                <span className="block truncate text-sm font-semibold leading-5 text-foreground">
                  {currentBrandTitle}
                </span>
                {currentBrandSubtitle ? (
                  <span className="block truncate text-xs leading-4 text-muted-foreground">
                    {currentBrandSubtitle}
                  </span>
                ) : null}
              </span>
              <ChevronDown
                className={cn(
                  "h-4 w-4 shrink-0 text-muted-foreground transition-transform",
                  isBrandSwitcherOpen && "rotate-180",
                )}
              />
            </>
          )}
        </button>

        {isBrandSwitcherOpen ? (
          <div
            className={cn(
              "absolute top-[calc(100%-0.75rem)] z-50 mt-3 overflow-hidden rounded-xl border border-border bg-card p-2 shadow-deep",
              collapsed ? "left-full ml-2 w-64" : "left-4 right-4",
            )}
          >
            <div className="max-h-80 space-y-1 overflow-y-auto">
              {brands.map((brand) => {
                const isSelected = brand.id === currentBrand?.id;
                const title = getBrandSwitcherTitle(brand);
                const subtitle = getBrandSwitcherSubtitle(brand, intl);
                return (
                  <Link
                    key={brand.id}
                    to={getBrandPathForStatus({
                      brandId: brand.id,
                      status: brand.status,
                      onboardingMode: brand.onboardingMode,
                      firstAssetReady: brand.onboarding.completion.firstAssetReady,
                      currentPathname: location.pathname,
                    })}
                    onClick={() => setIsBrandSwitcherOpen(false)}
                    className={cn(
                      "flex items-center gap-3 rounded-xl px-3 py-2.5 text-left transition-colors",
                      isSelected ? "bg-muted text-foreground" : "hover:bg-muted/70",
                    )}
                  >
                    <BrandMark brand={brand} title={title} size="sm" />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm font-semibold">{title}</span>
                      {subtitle ? (
                        <span className="block truncate text-xs text-muted-foreground">
                          {subtitle}
                        </span>
                      ) : null}
                    </span>
                  </Link>
                );
              })}
            </div>
            <div className="mt-2 border-t border-border pt-2">
              {addBrandDisabled ? (
                <div className="rounded-xl px-3 py-2 text-xs text-muted-foreground">
                  {intl.formatMessage(
                    {
                      id: "sidebar.brandSwitcher.limitReached",
                      defaultMessage: "Brand limit reached ({limit}).",
                    },
                    { limit: brandListQuery.data?.limit ?? 5 },
                  )}
                </div>
              ) : (
                <Link
                  to={addBrandHref}
                  onClick={() => setIsBrandSwitcherOpen(false)}
                  className="flex items-center gap-3 rounded-xl px-3 py-2 text-sm font-semibold text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground"
                >
                  <Plus className="h-4 w-4" />
                  <FormattedMessage
                    id="sidebar.brandSwitcher.addBrand"
                    defaultMessage="Add brand"
                  />
                </Link>
              )}
            </div>
          </div>
        ) : null}
      </div>

      {/* Navigation */}
      <nav className="flex min-h-0 flex-1 flex-col gap-1 overflow-y-auto px-3 pb-3 pt-3">
        {NAV_ITEMS.map((item) => {
          const href = sidebarBrandId ? buildBrandPath(sidebarBrandId, item.section) : "/app";
          const isDisabled = item.disabled || !canUseBrandNavigation;
          const isActive =
            routeBrandId !== undefined && location.pathname.startsWith(href) && href !== "#";
          const Icon = NAV_ITEM_ICONS[item.icon];
          return (
            <Link
              key={item.labelId}
              to={isDisabled ? location.pathname : href}
              onClick={preventBlockedBrandNavigation}
              className={cn(
                "flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
                collapsed && "relative justify-center px-2",
                isDisabled &&
                  "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
                isActive
                  ? "bg-muted text-foreground"
                  : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
              )}
              aria-disabled={isDisabled}
              aria-label={intl.formatMessage({
                id: item.labelId,
                defaultMessage: item.labelDefault,
              })}
              title={intl.formatMessage({ id: item.labelId, defaultMessage: item.labelDefault })}
            >
              <Icon className="h-4 w-4 shrink-0" strokeWidth={1.8} />
              {collapsed ? null : (
                <span className="flex-1">
                  {intl.formatMessage({ id: item.labelId, defaultMessage: item.labelDefault })}
                </span>
              )}
              {item.section === "/review" && pendingCount > 0 && (
                <span
                  className={cn(
                    "flex h-5 min-w-5 items-center justify-center rounded-full bg-brand px-1.5 text-[10px] font-extrabold text-brand-foreground",
                    collapsed && "absolute -right-1 -top-1 h-4 min-w-4 px-1 text-[9px]",
                  )}
                >
                  {pendingCount}
                </span>
              )}
            </Link>
          );
        })}

        {/* Brand Kit */}
        <button
          type="button"
          onClick={() => {
            if (!canUseBrandNavigation) {
              return;
            }

            if (collapsed) {
              onCollapsedChange(false);
              setIsBrandKitOpen(true);
              return;
            }

            setIsBrandKitOpen((prev) => !prev);
          }}
          className={cn(
            "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-semibold transition-colors",
            collapsed && "justify-center px-2",
            !canUseBrandNavigation &&
              "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
            isBrandKitRoute || isBrandKitOpen
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
          )}
          aria-expanded={isBrandKitOpen}
          aria-disabled={!canUseBrandNavigation}
          aria-label={intl.formatMessage({ id: "sidebar.brandKit", defaultMessage: "Brand Kit" })}
          title={intl.formatMessage({ id: "sidebar.brandKit", defaultMessage: "Brand Kit" })}
        >
          <Star className="h-4 w-4 shrink-0 text-current" strokeWidth={1.8} />
          {collapsed ? null : (
            <>
              <span className="flex min-w-0 flex-1 items-center justify-between gap-2 text-left">
                <span className="truncate">
                  <FormattedMessage id="sidebar.brandKit" defaultMessage="Brand Kit" />
                </span>
              </span>
              <ChevronDown
                className={cn(
                  "h-3.5 w-3.5 shrink-0 transition-transform",
                  isBrandKitOpen && "rotate-180",
                )}
              />
            </>
          )}
        </button>
        {isBrandKitOpen && !collapsed && (
          <div className="ml-7 flex flex-col gap-0.5 border-l border-border pl-3 pt-0.5">
            {BRAND_KIT_ITEMS.map(({ labelDefault, labelId, section }) => {
              const href = sidebarBrandId ? buildBrandPath(sidebarBrandId, section) : "/app";
              const isActive = routeBrandId !== undefined && location.pathname.startsWith(href);
              return (
                <Link
                  key={section}
                  to={canUseBrandNavigation ? href : location.pathname}
                  onClick={preventBlockedBrandNavigation}
                  className={cn(
                    "rounded-md px-3 py-2 text-sm font-medium transition-colors",
                    !canUseBrandNavigation &&
                      "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
                    isActive
                      ? "bg-muted/80 text-foreground"
                      : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
                  )}
                >
                  {intl.formatMessage({ id: labelId, defaultMessage: labelDefault })}
                </Link>
              );
            })}
          </div>
        )}
        {audienceProfileSelector}
        <div className="mt-3 px-0">
          <button
            type="button"
            className="flex w-full items-center justify-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5 text-sm font-bold text-foreground transition-colors hover:bg-muted/60 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-card"
            onClick={() => assetCreationGate.requestAssetCreation(() => setCreateOpen(true))}
            disabled={!canUseBrandNavigation || assetCreationGate.gateState === "loading"}
            aria-label={intl.formatMessage({
              id: "sidebar.createNew",
              defaultMessage: "Create New",
            })}
            title={intl.formatMessage({ id: "sidebar.createNew", defaultMessage: "Create New" })}
          >
            <Plus className="h-4 w-4 shrink-0" strokeWidth={2.2} />
            {collapsed ? null : (
              <FormattedMessage id="sidebar.createNew" defaultMessage="Create New" />
            )}
          </button>
        </div>
        {sidebarBrandId ? (
          <div className={cn("mt-3", collapsed ? "flex justify-center" : "space-y-1.5")}>
            {collapsed ? (
              <Link
                to={canUseBrandNavigation ? latestChatHref : location.pathname}
                onClick={preventBlockedBrandNavigation}
                className={cn(
                  "relative flex h-9 w-9 items-center justify-center rounded-lg text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground",
                  !canUseBrandNavigation &&
                    "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
                )}
                aria-disabled={!canUseBrandNavigation}
                aria-label={
                  hasUnseenAssistantContent
                    ? intl.formatMessage({
                        id: "sidebar.chats.unseenLabel",
                        defaultMessage: "Chats, new content available",
                      })
                    : intl.formatMessage({ id: "sidebar.chats", defaultMessage: "Chats" })
                }
                title={
                  hasUnseenAssistantContent
                    ? intl.formatMessage({
                        id: "sidebar.chats.unseenLabel",
                        defaultMessage: "Chats, new content available",
                      })
                    : intl.formatMessage({ id: "sidebar.chats", defaultMessage: "Chats" })
                }
              >
                <MessageSquare className="h-4 w-4" strokeWidth={1.8} aria-hidden="true" />
                {hasUnseenAssistantContent ? (
                  <span
                    className="absolute right-1 top-1 h-2 w-2 rounded-full bg-brand ring-2 ring-card"
                    aria-hidden="true"
                  />
                ) : null}
              </Link>
            ) : (
              <>
                <div className="flex items-center justify-between gap-2 px-3 pt-1">
                  <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">
                    <FormattedMessage id="sidebar.chats" defaultMessage="Chats" />
                    {hasUnseenAssistantContent ? (
                      <>
                        <span className="h-2 w-2 rounded-full bg-brand" aria-hidden="true" />
                        <span className="sr-only">
                          <FormattedMessage
                            id="sidebar.chats.newContent"
                            defaultMessage="New content available"
                          />
                        </span>
                      </>
                    ) : null}
                  </span>
                  <button
                    type="button"
                    className="flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted/70 hover:text-foreground disabled:cursor-not-allowed disabled:opacity-50"
                    onClick={createChat}
                    disabled={!canCreateChat}
                    aria-label={intl.formatMessage({
                      id: "pulse.chatHistory.createChat",
                      defaultMessage: "Create new chat",
                    })}
                    title={intl.formatMessage({
                      id: "pulse.chatHistory.createChat",
                      defaultMessage: "Create new chat",
                    })}
                  >
                    <Plus className="h-3.5 w-3.5" strokeWidth={2.1} aria-hidden="true" />
                  </button>
                </div>
                <div className="space-y-0.5">
                  {chatListQuery.isPending ? (
                    <div className="flex items-center gap-2 rounded-lg px-3 py-2 text-sm text-muted-foreground">
                      <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
                      <FormattedMessage
                        id="pulse.chatHistory.loading"
                        defaultMessage="Loading chats..."
                      />
                    </div>
                  ) : chats.length === 0 ? (
                    <div className="rounded-lg px-3 py-2 text-sm leading-5 text-muted-foreground">
                      <FormattedMessage
                        id="pulse.chatHistory.empty"
                        defaultMessage="Your next Cheetah conversation will appear here."
                      />
                    </div>
                  ) : (
                    chats.map((chat) => {
                      const chatHref = buildBrandAssistantChatPath(sidebarBrandId, chat.id);
                      const isChatActive = routeChatId === chat.id;
                      const isRenaming = renamingChatId === chat.id;

                      if (isRenaming) {
                        return (
                          <div
                            key={chat.id}
                            className={cn(
                              "flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm font-medium ring-1 ring-ring/30",
                              isChatActive
                                ? "bg-muted text-foreground"
                                : "bg-muted/60 text-foreground",
                            )}
                          >
                            <MessageSquare
                              className="h-3.5 w-3.5 shrink-0 text-current opacity-70"
                              strokeWidth={1.8}
                              aria-hidden="true"
                            />
                            <input
                              value={renameDraft}
                              maxLength={80}
                              autoFocus
                              onFocus={(event) => event.currentTarget.select()}
                              onChange={(event) => setRenameDraft(event.target.value)}
                              onBlur={() => void saveRename(chat)}
                              onKeyDown={(event) => handleRenameKeyDown(event, chat)}
                              className="min-w-0 flex-1 rounded-md border border-border bg-background px-2 py-1 text-sm font-medium text-foreground outline-none ring-0 transition focus:border-ring focus:ring-2 focus:ring-ring/25"
                              aria-label={intl.formatMessage({
                                id: "sidebar.chats.renameInput",
                                defaultMessage: "Rename chat",
                              })}
                            />
                          </div>
                        );
                      }

                      return (
                        <div
                          key={chat.id}
                          className="group relative"
                          onBlur={(event) => {
                            const nextFocus = event.relatedTarget;
                            if (
                              nextFocus instanceof Node &&
                              event.currentTarget.contains(nextFocus)
                            ) {
                              return;
                            }

                            setChatMenuId((currentMenuId) =>
                              currentMenuId === chat.id ? null : currentMenuId,
                            );
                          }}
                        >
                          <Link
                            to={canUseBrandNavigation ? chatHref : location.pathname}
                            onClick={preventBlockedBrandNavigation}
                            aria-current={isChatActive ? "page" : undefined}
                            aria-disabled={!canUseBrandNavigation}
                            className={cn(
                              "flex items-center gap-2 rounded-lg py-2 pl-3 pr-9 text-sm font-medium transition-colors",
                              !canUseBrandNavigation &&
                                "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
                              isChatActive
                                ? "bg-muted text-foreground"
                                : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
                            )}
                            title={chat.title}
                          >
                            <MessageSquare
                              className="h-3.5 w-3.5 shrink-0 text-current opacity-70"
                              strokeWidth={1.8}
                              aria-hidden="true"
                            />
                            <span className="min-w-0 flex-1 truncate">{chat.title}</span>
                            {chat.hasUnseenAssistantContent ? (
                              <>
                                <span
                                  className="h-2 w-2 shrink-0 rounded-full bg-brand"
                                  aria-hidden="true"
                                />
                                <span className="sr-only">
                                  <FormattedMessage
                                    id="sidebar.chats.newContent"
                                    defaultMessage="New content available"
                                  />
                                </span>
                              </>
                            ) : null}
                          </Link>
                          <button
                            type="button"
                            className={cn(
                              "absolute right-1.5 top-1.5 flex h-6 w-6 items-center justify-center rounded-md text-muted-foreground opacity-0 transition hover:bg-background/80 hover:text-foreground focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring/35 group-hover:opacity-100",
                              !canUseBrandNavigation && "pointer-events-none",
                              chatMenuId === chat.id &&
                                "bg-background/80 text-foreground opacity-100",
                            )}
                            disabled={!canUseBrandNavigation}
                            onClick={(event) => {
                              event.preventDefault();
                              event.stopPropagation();
                              setChatActionError(null);
                              setChatMenuId((currentMenuId) =>
                                currentMenuId === chat.id ? null : chat.id,
                              );
                            }}
                            aria-expanded={chatMenuId === chat.id}
                            aria-label={intl.formatMessage({
                              id: "sidebar.chats.openMenu",
                              defaultMessage: "Open chat actions",
                            })}
                            title={intl.formatMessage({
                              id: "sidebar.chats.openMenu",
                              defaultMessage: "Open chat actions",
                            })}
                          >
                            <MoreHorizontal className="h-4 w-4" aria-hidden="true" />
                          </button>
                          {chatMenuId === chat.id ? (
                            <div className="absolute right-0 top-8 z-40 w-36 overflow-hidden rounded-xl border border-border bg-card p-1.5 shadow-deep">
                              <button
                                type="button"
                                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted/70"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  startRenamingChat(chat);
                                }}
                              >
                                <PencilLine className="h-3.5 w-3.5" aria-hidden="true" />
                                <FormattedMessage
                                  id="sidebar.chats.rename"
                                  defaultMessage="Rename"
                                />
                              </button>
                              <button
                                type="button"
                                className="flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-sm font-medium text-destructive transition-colors hover:bg-destructive/10"
                                onClick={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setChatMenuId(null);
                                  setChatActionError(null);
                                  setDeleteCandidate(chat);
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5" aria-hidden="true" />
                                <FormattedMessage
                                  id="sidebar.chats.delete"
                                  defaultMessage="Delete"
                                />
                              </button>
                            </div>
                          ) : null}
                        </div>
                      );
                    })
                  )}
                  {canShowMoreChats ? (
                    <button
                      type="button"
                      className="mt-1 w-full rounded-lg px-3 py-1.5 text-left text-xs font-medium text-muted-foreground transition-colors hover:bg-muted/45 hover:text-foreground disabled:cursor-wait disabled:opacity-60"
                      disabled={isLoadingMoreChats}
                      onClick={() => void showMoreChats()}
                    >
                      <FormattedMessage id="sidebar.chats.showMore" defaultMessage="Show more" />
                    </button>
                  ) : null}
                  {chatActionError ? (
                    <div className="rounded-lg px-3 py-2 text-xs leading-5 text-destructive">
                      {chatActionError}
                    </div>
                  ) : null}
                </div>
              </>
            )}
          </div>
        ) : null}
        {showAssistantAction && onOpenAssistant ? (
          <div className="mt-2 px-0">
            <button
              type="button"
              className="flex w-full items-center justify-center gap-2 rounded-lg bg-primary px-3 py-2.5 text-sm font-bold text-primary-foreground shadow-sm transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-primary"
              onClick={onOpenAssistant}
              disabled={!canUseBrandNavigation}
              aria-label={intl.formatMessage({
                id: "sidebar.askCheetah",
                defaultMessage: "Ask Cheetah",
              })}
              title={intl.formatMessage({
                id: "sidebar.askCheetah",
                defaultMessage: "Ask Cheetah",
              })}
            >
              <Sparkles className="h-4 w-4 shrink-0" strokeWidth={2.2} />
              {collapsed ? null : (
                <FormattedMessage id="sidebar.askCheetah" defaultMessage="Ask Cheetah" />
              )}
            </button>
          </div>
        ) : null}
      </nav>

      {/* Bottom actions */}
      <div className="shrink-0 space-y-1 border-t border-border px-3 pb-4 pt-4">
        <Link
          to={canUseBrandNavigation ? integrationsHref : location.pathname}
          onClick={preventBlockedBrandNavigation}
          className={cn(
            "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            collapsed && "justify-center px-2",
            !canUseBrandNavigation &&
              "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
            isIntegrationsActive
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
          )}
          aria-label={intl.formatMessage({
            id: "sidebar.integrations",
            defaultMessage: "Integrations",
          })}
          aria-disabled={!canUseBrandNavigation}
          title={intl.formatMessage({ id: "sidebar.integrations", defaultMessage: "Integrations" })}
        >
          <Plug className="h-4 w-4 shrink-0" strokeWidth={1.8} />
          {collapsed ? null : (
            <FormattedMessage id="sidebar.integrations" defaultMessage="Integrations" />
          )}
        </Link>
        {hasInstagramConnection && !collapsed ? (
          <Link
            to={canUseBrandNavigation ? instagramIntegrationHref : location.pathname}
            onClick={preventBlockedBrandNavigation}
            className={cn(
              "ml-7 flex items-center gap-2 rounded-md border-l border-border px-3 py-2 text-sm font-medium transition-colors",
              !canUseBrandNavigation &&
                "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
              isInstagramIntegrationActive
                ? "bg-muted/80 text-foreground"
                : "text-muted-foreground hover:bg-muted/60 hover:text-foreground",
            )}
          >
            <ImageIcon className="h-3.5 w-3.5 shrink-0" strokeWidth={1.8} />
            <FormattedMessage id="sidebar.instagram" defaultMessage="Instagram" />
          </Link>
        ) : null}
        <Link
          to={
            canUseBrandNavigation && sidebarBrandId
              ? buildBrandPath(sidebarBrandId, "/settings")
              : location.pathname
          }
          onClick={preventBlockedBrandNavigation}
          className={cn(
            "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors",
            collapsed && "justify-center px-2",
            !canUseBrandNavigation &&
              "cursor-not-allowed opacity-40 hover:bg-transparent hover:text-muted-foreground",
            routeBrandId &&
              isSidebarNestedPathActive(
                location.pathname,
                buildBrandPath(routeBrandId, "/settings"),
              )
              ? "bg-muted text-foreground"
              : "text-muted-foreground hover:bg-muted/70 hover:text-foreground",
          )}
          aria-label={intl.formatMessage({ id: "sidebar.settings", defaultMessage: "Settings" })}
          aria-disabled={!canUseBrandNavigation}
          title={intl.formatMessage({ id: "sidebar.settings", defaultMessage: "Settings" })}
        >
          <Settings className="h-4 w-4 shrink-0" strokeWidth={1.8} />
          {collapsed ? null : <FormattedMessage id="sidebar.settings" defaultMessage="Settings" />}
        </Link>
        <SidebarProfileMenu user={user} brandId={sidebarBrandId} collapsed={collapsed} />
      </div>
      <SidebarCreateModal
        brandId={sidebarBrandId}
        brandName={currentBrandTitle}
        open={createOpen}
        onOpenChange={setCreateOpen}
        onUpgradeRequired={assetCreationGate.openUpgradeModal}
        upgradeLabel={assetCreationGate.upgradeLabel}
      />
      {deleteCandidate ? (
        <div className="fixed inset-0 z-[70] flex items-center justify-center bg-background/75 p-4 backdrop-blur-sm">
          <Card
            className="w-full max-w-md rounded-3xl border-border/70 shadow-xl"
            role="dialog"
            aria-modal="true"
            aria-labelledby="delete-chat-title"
          >
            <CardContent className="space-y-5 p-6">
              <div className="flex items-start gap-3">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-destructive/10 text-destructive">
                  <Trash2 className="h-5 w-5" aria-hidden="true" />
                </div>
                <div className="min-w-0">
                  <h2
                    id="delete-chat-title"
                    className="text-lg font-semibold tracking-tight text-foreground"
                  >
                    <FormattedMessage
                      id="sidebar.chats.deleteTitle"
                      defaultMessage="Delete chat?"
                    />
                  </h2>
                  <p className="mt-1 text-sm leading-6 text-muted-foreground">
                    <FormattedMessage
                      id="sidebar.chats.deleteDescription"
                      defaultMessage={
                        'This will permanently delete "{title}" and its chat history. This action cannot be undone.'
                      }
                      values={{ title: deleteCandidate.title }}
                    />
                  </p>
                </div>
              </div>

              {chatActionError ? (
                <div className="rounded-2xl border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
                  {chatActionError}
                </div>
              ) : null}

              <div className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    setDeleteCandidate(null);
                    setChatActionError(null);
                  }}
                  disabled={deleteChatMutation.isPending}
                >
                  <FormattedMessage id="common.cancel" defaultMessage="Cancel" />
                </Button>
                <Button
                  type="button"
                  variant="destructive"
                  onClick={() => void confirmDeleteChat()}
                  disabled={deleteChatMutation.isPending}
                >
                  {deleteChatMutation.isPending ? (
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                  ) : null}
                  <FormattedMessage id="sidebar.chats.confirmDelete" defaultMessage="Delete" />
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      ) : null}
      {assetCreationGate.upgradeModal}
    </aside>
  );
}
